/**
 * Template demo **"RELATÓRIO DE HABILIDADES — Educação Infantil"**, baseado no
 * modelo real do Colégio Santa Maria (2026).
 *
 * Estrutura:
 * - Eixos = **campos de experiência da BNCC** (5) + áreas de especialistas
 *   (Música, Língua Inglesa, Movimento e Corporeidade/Ed. Física).
 * - Indexes = habilidades descritas em cada campo.
 * - Paleta de conceitos = **C / CO / AC / NA** (Conquistou / Conquistando /
 *   A conquistar / Não avaliado). `Sem conceito` é anexado pelo serviço.
 * - Períodos = 4 bimestres (no demo só o 1º traz dados seed; os demais ficam
 *   em branco para o professor preencher).
 *
 * Integração: o fixture é ativado por `String(idTurmaDisc) === String(DEMO_TURMA_DISC_HAB)`
 * em `buildDemoIndividualReport.js` e `IndividualReportService.getConcepts`, de
 * modo que a seleção da "disciplina" `RELATÓRIO DE HABILIDADES` no editor
 * automaticamente troca eixos, habilidades e paleta.
 */

import { buildDemoStudentsForClass } from "./buildDemoStudents";
import { DEMO_TURMA_DISC_HAB } from "./buildDemoOccurrences";

/** Id/label dos conceitos do relatório de habilidades. */
export const DEMO_HAB_CONCEPTS = [
  {
    id: "hab_c",
    label: "C – Conquistou",
    color: "#c8e6c9",
    idConcept: 9401,
    idGroup: 40
  },
  {
    id: "hab_co",
    label: "CO – Conquistando",
    color: "#fff9c4",
    idConcept: 9402,
    idGroup: 40
  },
  {
    id: "hab_ac",
    label: "AC – A conquistar",
    color: "#ffe0b2",
    idConcept: 9403,
    idGroup: 40
  },
  {
    id: "hab_na",
    label: "NA – Não avaliado",
    color: "#eceff1",
    idConcept: 9404,
    idGroup: 40
  }
];

/**
 * Eixos e habilidades — transcrição direta do PDF do Colégio Santa Maria
 * (Pré D, 2026). Os campos `TIPOETAPA/CODETAPA/CODPROVA` mantêm o contrato
 * atual do endpoint legado (ver `IndividualReport.save`).
 */
export const DEMO_HAB_AXIS = [
  {
    name: "Escuta, fala, pensamento e imaginação",
    indexes: [
      "Identifica e nomeia todas as letras do nome próprio.",
      "Escreve o nome próprio convencionalmente.",
      "Identifica todas as letras do alfabeto (na sequência e fora dela).",
      "Nomeia todas as letras do alfabeto (na sequência e fora dela).",
      "Reconhece palavras significativas do projeto, nome dos amigos, palavras da jornada diária etc.",
      "Faz uso de sílabas simples em suas tentativas de escrita.",
      "Identifica, oralmente, a quantidade de sílabas nas palavras.",
      "Verbaliza palavras que iniciem com a sílaba proposta.",
      "Monta palavras com as sílabas dadas.",
      "Traça as letras de forma legível.",
      "Realiza registros com coerência, de acordo com o que foi solicitado.",
      "Estabelece relação sonora com a sílaba inicial de palavras significativas do grupo (Ex.: RODA – ROBERTA).",
      "Localiza palavras em listas com letras iniciais diferentes (Rita, Luísa, Pedro).",
      "Localiza palavras em listas com letras iniciais iguais (Laís, Letícia, Lorena).",
      "Estabelece relação som-letra (fonema/grafema) em nomes, sílabas simples e escritas de autoria.",
      "Busca estratégias para realizar a leitura de palavras significativas trabalhadas.",
      "Realiza tentativas de leitura de palavras com sílabas simples.",
      "Percebe a sonoridade das sílabas em rimas, aliterações, cantigas, parlendas, versos etc.",
      "Participa das comunicações orais de acordo com os procedimentos combinados (esperar a vez, ouvir o colega, manter o foco).",
      "Expressa ideias e opiniões de modo a ser compreendido com pertinência aos temas abordados.",
      "Pronuncia palavras corretamente, sem trocas ou omissões de fonemas.",
      "Participa dos momentos de pesquisa e investigação / projetos.",
      "Comunica aprendizagens relativas ao projeto da série ou outras investigações.",
      "Participa dos momentos de inglês lúdico (língua adicional)."
    ]
  },
  {
    name: "Espaços, tempos, quantidades, relações e transformações",
    indexes: [
      "Reconhece e nomeia as figuras geométricas planas (círculo, quadrado, retângulo e triângulo).",
      "Utiliza a linguagem matemática nas situações cotidianas: maior, menor, mais, menos, igual, diferente etc.",
      "Utiliza estratégias matemáticas para resolver problemas quanto a: tamanho, forma, cor, espessura, quantidade etc.",
      "Resolve problemas numéricos e não-numéricos usando estratégias lógicas/matemáticas.",
      "Soluciona situações-problema sem a interferência direta dos adultos.",
      "Identifica e nomeia os numerais do 1 ao 30.",
      "Conta de maneira organizada, adequando a recitação ao elemento contado e relaciona quantidades aos algarismos.",
      "Recita os números de 1 a 31 (com apoio do calendário).",
      "Representa quantidades graficamente.",
      "Traça numerais de forma legível.",
      "Demonstra curiosidade para buscar informações e respostas para suas dúvidas.",
      "Participa com interesse das situações de pesquisa, investigações, experimentações e levantamento de hipóteses.",
      "Compreende o trabalho desenvolvido com o gráfico / tratamento de informação.",
      "Faz uso do calendário como suporte para memória e percepção da passagem do tempo."
    ]
  },
  {
    name: "O eu, o outro e o nós",
    indexes: [
      "Cuida de seu corpo e realiza a higiene pessoal de maneira autônoma.",
      "Relata aprendizagens com pertinência a partir de experiências e materiais.",
      "Participa e coopera nas propostas coletivas.",
      "Demonstra independência ao deslocar-se nos diferentes espaços do colégio.",
      "Manifesta atitudes de respeito e afeto.",
      "Respeita momentos de orações espontâneas nas situações cotidianas (lanche, aniversário e celebração).",
      "Respeita regras de convívio social.",
      "Faz uso de aprendizagens socioemocionais (solidariedade, cooperação, escuta, diálogo, empatia) para resolver desentendimentos.",
      "Participa com interesse de propostas coletivas (sustentabilidade, preservação do meio ambiente, Campanha da Fraternidade, Pacto Global).",
      "Demonstra relação de cuidado e respeito à preservação da natureza.",
      'Envolve-se nas atividades relacionadas a "Campanha da Fraternidade".',
      "Organiza materiais de uso pessoal e coletivo.",
      "Sustenta a atenção durante as propostas."
    ]
  },
  {
    name: "Traços, sons, cores e formas",
    indexes: [
      "Utiliza adequadamente materiais como: tinta, nanquim, giz de cera, lápis de cor, canetinha etc.",
      "Comunica ideias fazendo uso de narrativas do desenho, com empenho e coerência.",
      "Desenha a figura humana estruturada — cabeça, tronco, membros e detalhes.",
      "Valoriza e respeita suas produções artísticas e as dos colegas.",
      "Aprecia e participa das situações de brincadeiras de roda, brincadeiras cantadas e jogos musicais.",
      "Vivencia jogos teatrais, dramáticos e simbólicos.",
      "Aprecia obras de arte e inclui elementos das obras trabalhadas em suas produções."
    ]
  },
  {
    name: "Corpo, gestos e movimentos",
    indexes: [
      "Segura corretamente materiais e suportes utilizando o polegar e o indicador (movimento de pinça), com a mão, sobre o papel.",
      "Apresenta traçado regular e com firmeza ao desenhar, grafar e pintar.",
      "Demonstra controle e adequação do uso do seu corpo nas diferentes situações (circuito, desafios corporais e jogos motores).",
      "Acompanha os movimentos rítmicos e corporais das brincadeiras de roda, jogos teatrais e motores.",
      "Tenta superar desafios motores propostos.",
      "Realiza recortes segurando e utilizando corretamente a tesoura."
    ]
  },
  {
    name: "Língua Inglesa",
    indexes: [
      "Socializa com o grupo durante as brincadeiras em inglês, valorizando as interações.",
      "Participa de comunicação oral em inglês (esperar a vez, ouvir o colega, manter o foco no tema).",
      "Reconhece os vocabulários novos inseridos na rotina em inglês.",
      "Faz uso das expressões de saudação em inglês.",
      "Ouve histórias relacionando as imagens com palavras em inglês.",
      "Registra as histórias lidas de acordo com o que foi solicitado, identificando personagens.",
      "Acompanha os comandos em inglês em situações de teatralidade, musicalidade e corporeidade.",
      "Realiza as atividades com compreensão de assuntos trabalhados em inglês.",
      "Faz uso do acervo de memória (palavras, expressões, músicas e cantigas).",
      "Incorpora palavras e expressões ao vocabulário.",
      "Entende a orientação da professora em atividades como roda, passeios, jogos etc."
    ]
  },
  {
    name: "Movimento e Corporeidade — Educação Física",
    indexes: [
      "Corre com desenvoltura e evita colisões nos jogos de pegar e fugir.",
      "Salta sobre obstáculos (amarelinhas feitas com bambolês, cordas elásticas etc.).",
      "Executa os esportes adaptados e suas habilidades específicas (arremessar e chutar).",
      "Realiza cambalhota para frente com equilíbrio e segurança.",
      "Percorre e equilibra-se em diferentes superfícies com controle motor (trave de equilíbrio, pneu, cama elástica, banco sueco).",
      "Identifica e representa diferentes ritmos musicais, dançando, cantando e fazendo uso de percussão.",
      "Cria movimentos e formas de atuar nos jogos simbólicos e nas propostas psicomotoras.",
      "Pula corda coletivamente respeitando o ritmo.",
      "Realiza atividades diferenciando propostas onde o foco é a cooperação ou a competição.",
      "Relaciona-se com os diferentes grupos formados nas aulas e mostra-se cooperativo.",
      "Resolve conflitos por meio do diálogo.",
      "Respeita as regras e os combinados.",
      "Mantém a escuta durante as orientações em roda.",
      "Mostra-se persistente na execução das atividades, brincadeiras e jogos."
    ]
  },
  {
    name: "Música",
    indexes: [
      "Participa ativamente nas atividades propostas.",
      "Memoriza as canções propostas nas aulas.",
      "Reconhece diferentes tipos de sons produzidos por fontes sonoras que nos rodeiam.",
      "Acompanha na pulsação diferentes obras musicais."
    ]
  }
];

/** Áreas com códigos legados (para `save` no endpoint atual). */
const AREA_CODES = {
  "Escuta, fala, pensamento e imaginação": "EFI",
  "Espaços, tempos, quantidades, relações e transformações": "ETQ",
  "O eu, o outro e o nós": "EON",
  "Traços, sons, cores e formas": "TSC",
  "Corpo, gestos e movimentos": "CGM",
  "Língua Inglesa": "LIN",
  "Movimento e Corporeidade — Educação Física": "EDF",
  Música: "MUS"
};

/** Converte os rótulos de habilidades em objetos com ids estáveis. */
function buildIndexes() {
  return DEMO_HAB_AXIS.map(ax => ({
    name: ax.name,
    indexes: ax.indexes.map((label, i) => ({
      id: `hab-${AREA_CODES[ax.name] || "GEN"}-${i + 1}`,
      name: label,
      TIPOETAPA: "N",
      CODETAPA: "1BIM",
      CODPROVA: AREA_CODES[ax.name] || "GEN"
    }))
  }));
}

const INDEXED_AXIS = buildIndexes();

/**
 * Valores do 1º bimestre para a aluna 1 (Ana Carolina Silva — espelha o
 * modelo real do PDF para Isadora). Chave: `${areaCode}-${indexNumber}`.
 * Índices ausentes ficam sem conceito.
 */
const STUDENT_1_SEED = {
  "EFI-1": "hab_c",
  "EFI-2": "hab_c",
  "EFI-3": "hab_co",
  "EFI-4": "hab_co",
  "EFI-5": "hab_na",
  "EFI-6": "hab_na",
  "EFI-7": "hab_na",
  "EFI-8": "hab_na",
  "EFI-9": "hab_na",
  "EFI-10": "hab_na",
  "EFI-11": "hab_co",
  "EFI-12": "hab_na",
  "EFI-13": "hab_na",
  "EFI-14": "hab_na",
  "EFI-15": "hab_na",
  "EFI-16": "hab_na",
  "EFI-17": "hab_na",
  "EFI-18": "hab_na",
  "EFI-19": "hab_co",
  "EFI-20": "hab_c",
  "EFI-21": "hab_c",
  "EFI-22": "hab_c",
  "EFI-23": "hab_c",
  "EFI-24": "hab_c",

  "ETQ-1": "hab_na",
  "ETQ-2": "hab_c",
  "ETQ-3": "hab_na",
  "ETQ-4": "hab_na",
  "ETQ-5": "hab_na",
  "ETQ-6": "hab_na",
  "ETQ-7": "hab_co",
  "ETQ-8": "hab_na",
  "ETQ-9": "hab_c",
  "ETQ-10": "hab_na",
  "ETQ-11": "hab_c",
  "ETQ-12": "hab_c",
  "ETQ-13": "hab_na",
  "ETQ-14": "hab_na",

  "EON-1": "hab_co",
  "EON-2": "hab_c",
  "EON-3": "hab_c",
  "EON-4": "hab_c",
  "EON-5": "hab_c",
  "EON-6": "hab_c",
  "EON-7": "hab_c",
  "EON-8": "hab_c",
  "EON-9": "hab_c",
  "EON-10": "hab_c",
  "EON-11": "hab_c",
  "EON-12": "hab_c",
  "EON-13": "hab_co",

  "TSC-1": "hab_c",
  "TSC-2": "hab_co",
  "TSC-3": "hab_c",
  "TSC-4": "hab_c",
  "TSC-5": "hab_c",
  "TSC-6": "hab_c",
  "TSC-7": "hab_c",

  "CGM-1": "hab_c",
  "CGM-2": "hab_c",
  "CGM-3": "hab_c",
  "CGM-4": "hab_c",
  "CGM-5": "hab_c",
  "CGM-6": "hab_na",

  "LIN-1": "hab_c",
  "LIN-2": "hab_c",
  "LIN-3": "hab_c",
  "LIN-4": "hab_c",
  "LIN-5": "hab_c",
  "LIN-6": "hab_c",
  "LIN-7": "hab_c",
  "LIN-8": "hab_c",
  "LIN-9": "hab_c",
  "LIN-10": "hab_c",
  "LIN-11": "hab_c",

  "EDF-1": "hab_c",
  "EDF-2": "hab_c",
  "EDF-3": "hab_c",
  "EDF-4": "hab_na",
  "EDF-5": "hab_c",
  "EDF-6": "hab_c",
  "EDF-7": "hab_c",
  "EDF-8": "hab_na",
  "EDF-9": "hab_c",
  "EDF-10": "hab_c",
  "EDF-11": "hab_c",
  "EDF-12": "hab_c",
  "EDF-13": "hab_c",
  "EDF-14": "hab_c",

  "MUS-1": "hab_c",
  "MUS-2": "hab_c",
  "MUS-3": "hab_c",
  "MUS-4": "hab_c"
};

/**
 * Rotação determinística usada para os demais alunos — distribui a paleta de
 * forma plausível (majoritariamente "C" com "CO" e "AC" ocasionais, "NA"
 * minoritário).
 */
const CONCEPT_ROTATION = [
  "hab_c",
  "hab_c",
  "hab_c",
  "hab_co",
  "hab_c",
  "hab_ac",
  "hab_c",
  "hab_co",
  "hab_c",
  "hab_na"
];

/**
 * GET periods (4 bimestres; 1º marcado como `current`). O demo só traz dados
 * para o 1º — os demais voltam vazios para preenchimento.
 */
export function buildDemoHabReportPeriods() {
  return [
    { id: "1BIM", name: "1º bimestre", current: true },
    { id: "2BIM", name: "2º bimestre", current: false },
    { id: "3BIM", name: "3º bimestre", current: false },
    { id: "4BIM", name: "4º bimestre", current: false }
  ];
}

/** GET concepts — paleta C/CO/AC/NA. */
export function buildDemoHabReportConcepts() {
  return DEMO_HAB_CONCEPTS;
}

/**
 * Extrai o código e o número do id do índice (`hab-EFI-3` → `{ area: "EFI", num: 3 }`).
 */
function parseIndexId(id) {
  const [, area, num] = id.split("-");
  return { area, num: parseInt(num, 10) };
}

/**
 * GET report data — corpo esperado por `IndividualReport.setupTableProps`.
 * Aluno #1 recebe os valores do PDF; os demais, rotação determinística.
 * Em períodos que não sejam o 1º bimestre, tudo retorna vazio.
 */
export function buildDemoHabReportData(idTurmaDisc, period) {
  if (String(idTurmaDisc) !== String(DEMO_TURMA_DISC_HAB)) {
    return { axis: [], students: [] };
  }

  const students = buildDemoStudentsForClass("DEMO-T1").map((s, i) => {
    const concepts = INDEXED_AXIS.flatMap(ax => ax.indexes).map((ix, j) => {
      if (period && period !== "1BIM") {
        return { id: ix.id, value: null };
      }
      if (i === 0) {
        const key = `${parseIndexId(ix.id).area}-${parseIndexId(ix.id).num}`;
        return { id: ix.id, value: STUDENT_1_SEED[key] || null };
      }
      return {
        id: ix.id,
        value: CONCEPT_ROTATION[(i + j) % CONCEPT_ROTATION.length]
      };
    });
    return {
      rollCallNum: i + 1,
      ra: s.ra,
      name: s.name,
      photo: s.photo,
      absense: 0,
      concepts
    };
  });

  return { axis: INDEXED_AXIS, students };
}

/** Metadados expostos ao `IndividualReportList` (um item por bimestre). */
export function buildDemoHabReportListEntries(classId) {
  const periods = buildDemoHabReportPeriods();
  const bim1 = buildDemoHabReportData(DEMO_TURMA_DISC_HAB, "1BIM");
  const totalIndexes = bim1.axis.reduce(
    (acc, ax) => acc + ax.indexes.length,
    0
  );
  const totalStudents = bim1.students.length;
  const totalCells = totalStudents * totalIndexes;

  const filledCellsBim1 = bim1.students.reduce((acc, s) => {
    const filled = (s.concepts || []).filter(c => c.value).length;
    return acc + filled;
  }, 0);
  const filledPercentBim1 =
    totalCells === 0
      ? 0
      : Math.round((filledCellsBim1 / totalCells) * 100) / 100;

  return periods.map(period => ({
    reportKey: `${DEMO_TURMA_DISC_HAB}::${period.id}`,
    classId,
    discipline: {
      idTurmaDisc: DEMO_TURMA_DISC_HAB,
      name: "RELATÓRIO DE HABILIDADES — Educação Infantil"
    },
    period,
    studentsCount: totalStudents,
    indexesCount: totalIndexes,
    filledPercent: period.id === "1BIM" ? filledPercentBim1 : 0,
    updatedAt: period.id === "1BIM" ? new Date().toISOString() : null
  }));
}
